/**
 * advisorContext.ts — client-side CONTEXT assembly for the AI Strategy Advisor (T-501)
 *
 * Turns the map/panel state the advisor needs into the size-bounded, null-tolerant
 * AdvisorContext (design §3.3) that rides with each chat turn. Kept a PURE function (no React,
 * no hooks) so grounding correctness is unit-testable in isolation.
 *
 * Grounding note: the CONTEXT block is the ONLY app-data the model sees (design §2.1 Layer B),
 * so this mapping is the numeric surface. Scores are rounded to integers; optional sub-scores
 * that are absent/NaN in a sparse bbox snapshot become `null` (the advisor reads null as "not
 * available in the current view"); the list is capped so an over-dense viewport can't inflate
 * the payload. `tier` reuses the panel's `getTierWord` so the word the advisor uses is exactly
 * the word the UI shows (live-app tiers 70/60/45/35). Bottom-tracts are deferred for v1:
 * CONTEXT carries the top-N + the active tract only.
 *
 * Enriched (Sep 2026): site-level sub-scores, state percentiles, demographics, nearby charger
 * details, and component scores — so the advisor can give investment-quality site analysis.
 */
import { getTierWord } from './scoreTiers'
import type { CensusTract, SiteScoreResult } from './tractModel'
import type { MapTier } from './mapTiers'
import type { NearbyStats } from './nearbyStats'

/** Cap on ranked in-view tracts included in CONTEXT (design §3.2). */
export const MAX_TOP_TRACTS = 8

/** One transcript turn sent to the advisor endpoint. */
export interface AdvisorMessage {
  role: 'user' | 'assistant'
  content: string
}

/** Nearby charger ring summary for the advisor (mirrors NearbyRing, trimmed for payload size). */
interface AdvisorNearbyRing {
  radiusMi: number
  stations: number
  dcFastStations: number
  l2Ports: number
  dcFastPorts: number
  totalPorts: number
  networks: number
  nacsStations: number
  topNetwork: string | null
  topNetworkPortShare: number
}

/** Nearest station summary for the advisor. */
interface AdvisorNearestStation {
  network: string
  name: string
  distanceMi: number
  portsLevel2: number
  portsDcFast: number
}

/** The size-bounded, null-tolerant app-state block sent with each advisor turn (design §3.3). */
export interface AdvisorContext {
  focus: {
    address: string | null
    lat: number | null
    lng: number | null
    geocodeConfidence: 'exact' | 'approximate' | null
  } | null
  currentView: 'map'
  mapTier: MapTier
  activeTract: {
    geoid: string
    opportunityScore: number
    tier: string
    demandPotential: number | null
    marketPressure: number | null
    pricingPower: number | null
    gap: number | null
    evRegPer1000: number | null
    // Enriched fields — optional so existing test fixtures compile without listing every field
    cdpiPctileState?: number | null
    cmpiPctileState?: number | null
    gapPctileState?: number | null
    ppiPctileState?: number | null
    dominantType?: string | null
    CS?: number | null
    RDS?: number | null
    HCC?: number | null
    HCO?: number | null
    DTS?: number | null
    medianHhIncome?: number | null
    populationDensity?: number | null
    pctRenter?: number | null
    multifamilyShare?: number | null
    pctCollegeEducated?: number | null
    medianHomeValue?: number | null
    sfDetachedShare?: number | null
    pctOwnerOccupied?: number | null
    jobsRetailPct?: number | null
    jobsFoodAccommodationPct?: number | null
    jobsEntertainmentPct?: number | null
    daytimePopRatio?: number | null
    stationCount?: number | null
    portsTotal?: number | null
    dcfcPorts?: number | null
    corridorExposureScore?: number | null
  } | null
  siteScore: {
    opportunityScore: number
    lviBaseline: number
    degraded: boolean
    // Enriched — optional for backward compat
    siteCdpi?: number | null
    siteCmpi?: number | null
    sitePpi?: number | null
    gapScore?: number | null
    rampDistanceMi?: number | null
    poiCount?: number | null
    competition?: { weighted: number; distinctNetworks: number } | null
  } | null
  nearby?: {
    rings: AdvisorNearbyRing[]
    nearest: AdvisorNearestStation | null
    nearestAlt: AdvisorNearestStation | null
    amenitiesAreFloor: boolean
  } | null
  topTractsInView: Array<{ geoid: string; rank: number; opportunityScore: number }>
}

/** The plain state buildAdvisorContext maps from (MapShell state + ranked in-view rows). */
export interface AdvisorContextInput {
  activeTract: CensusTract | null
  siteScore: SiteScoreResult | null
  activeLocation: { lat: number; lng: number } | null
  searchedAddress: string | null
  geocodeConfidence: 'exact' | 'approximate' | null
  mapTier: MapTier
  /** Ranked in-view tracts (simplified from RankedRow: geoid, 1-based rank, LVI/opportunity). */
  topTracts: Array<{ geoid: string; rank: number; lvi: number }>
  /** Nearby charger stats (fetched when the advisor panel opens). Null before fetch completes. */
  nearby?: NearbyStats | null
}

/** Round to an integer, or null when the value is absent/NaN (sparse bbox snapshot). */
function roundOrNull(v: number | null | undefined): number | null {
  return v == null || Number.isNaN(v) ? null : Math.round(v)
}

/** Round to 2 decimal places, or null. Used for component scores (0–1 range) and distances. */
function round2OrNull(v: number | null | undefined): number | null {
  return v == null || Number.isNaN(v) ? null : Math.round(v * 100) / 100
}

/** Round a coordinate to ~5 decimals (≈1 m) to bound payload size without losing the point. */
function coord(v: number): number {
  return Math.round(v * 1e5) / 1e5
}

/** Summarize a NearestStation for the advisor payload (drop hasNacs — redundant with ring data). */
function summarizeNearest(
  s: {
    network: string
    name: string
    distanceMi: number
    portsLevel2: number
    portsDcFast: number
  } | null,
): AdvisorNearestStation | null {
  if (!s) return null
  return {
    network: s.network,
    name: s.name,
    distanceMi: s.distanceMi,
    portsLevel2: s.portsLevel2,
    portsDcFast: s.portsDcFast,
  }
}

export function buildAdvisorContext(input: AdvisorContextInput): AdvisorContext {
  const { activeTract, siteScore, activeLocation, searchedAddress, geocodeConfidence } = input

  const focus =
    activeLocation || searchedAddress || geocodeConfidence
      ? {
          address: searchedAddress ?? null,
          lat: activeLocation ? coord(activeLocation.lat) : null,
          lng: activeLocation ? coord(activeLocation.lng) : null,
          geocodeConfidence: geocodeConfidence ?? null,
        }
      : null

  // Build nearby summary from NearbyStats (if available)
  const nearby = input.nearby
    ? {
        rings: input.nearby.rings.map((r) => ({
          radiusMi: r.radiusMi,
          stations: r.stations,
          dcFastStations: r.dcFastStations,
          l2Ports: r.l2Ports,
          dcFastPorts: r.dcFastPorts,
          totalPorts: r.totalPorts,
          networks: r.networks,
          nacsStations: r.nacsStations,
          topNetwork: r.topNetwork,
          topNetworkPortShare: Math.round(r.topNetworkPortShare * 100) / 100,
        })),
        nearest: summarizeNearest(input.nearby.nearest),
        nearestAlt: summarizeNearest(input.nearby.nearestAlt),
        amenitiesAreFloor: input.nearby.amenitiesAreFloor,
      }
    : null

  return {
    focus,
    currentView: 'map',
    mapTier: input.mapTier,
    activeTract: activeTract
      ? {
          geoid: activeTract.tractId,
          opportunityScore: Math.round(activeTract.lviScore),
          tier: getTierWord(activeTract.lviScore),
          demandPotential: roundOrNull(activeTract.cdpiScore),
          marketPressure: roundOrNull(activeTract.cmpiScore),
          pricingPower: roundOrNull(activeTract.ppiScore),
          gap: roundOrNull(activeTract.gapScore),
          evRegPer1000: roundOrNull(activeTract.evRegPer1000),
          // State percentiles
          cdpiPctileState: roundOrNull(activeTract.cdpiPctileState),
          cmpiPctileState: roundOrNull(activeTract.cmpiPctileState),
          gapPctileState: roundOrNull(activeTract.gapPctileState),
          ppiPctileState: roundOrNull(activeTract.ppiPctileState),
          // Classification
          dominantType: activeTract.dominantType ?? null,
          // Component scores (keep 2 decimal places — 0–1 range)
          CS: round2OrNull(activeTract.CS),
          RDS: round2OrNull(activeTract.RDS),
          HCC: round2OrNull(activeTract.HCC),
          HCO: round2OrNull(activeTract.HCO),
          DTS: round2OrNull(activeTract.DTS),
          // Demographics
          medianHhIncome: roundOrNull(activeTract.medianHhIncome),
          populationDensity: roundOrNull(activeTract.populationDensity),
          pctRenter: round2OrNull(activeTract.pctRenter),
          multifamilyShare: round2OrNull(activeTract.multifamilyShare),
          pctCollegeEducated: round2OrNull(activeTract.pctCollegeEducated),
          medianHomeValue: roundOrNull(activeTract.medianHomeValue),
          sfDetachedShare: round2OrNull(activeTract.sfDetachedShare),
          pctOwnerOccupied: round2OrNull(activeTract.pctOwnerOccupied),
          // Job mix
          jobsRetailPct: round2OrNull(activeTract.jobsRetailPct),
          jobsFoodAccommodationPct: round2OrNull(activeTract.jobsFoodAccommodationPct),
          jobsEntertainmentPct: round2OrNull(activeTract.jobsEntertainmentPct),
          daytimePopRatio: round2OrNull(activeTract.daytimePopRatio),
          // Infrastructure
          stationCount: roundOrNull(activeTract.stationCount),
          portsTotal: roundOrNull(activeTract.portsTotal),
          dcfcPorts: roundOrNull(activeTract.dcfcPorts),
          corridorExposureScore: round2OrNull(activeTract.corridorExposureScore),
        }
      : null,
    siteScore: siteScore
      ? {
          opportunityScore: Math.round(siteScore.opportunityScore),
          lviBaseline: Math.round(siteScore.lviBaseline),
          degraded: siteScore.v2?.degraded ?? false,
          // Site-level sub-scores from v2
          siteCdpi: roundOrNull(siteScore.v2?.siteCdpi),
          siteCmpi: roundOrNull(siteScore.v2?.siteCmpi),
          sitePpi: roundOrNull(siteScore.v2?.sitePpi),
          gapScore: roundOrNull(siteScore.v2?.gapScore),
          // Site adjustments
          rampDistanceMi: round2OrNull(siteScore.v2?.rampDistanceMi),
          poiCount: siteScore.v2?.poiCount ?? null,
          // Competition
          competition: siteScore.v2?.competition
            ? {
                weighted: Math.round(siteScore.v2.competition.weighted * 10) / 10,
                distinctNetworks: siteScore.v2.competition.distinctNetworks,
              }
            : null,
        }
      : null,
    nearby,
    topTractsInView: input.topTracts.slice(0, MAX_TOP_TRACTS).map((r) => ({
      geoid: r.geoid,
      rank: r.rank,
      opportunityScore: Math.round(r.lvi),
    })),
  }
}
